// usePOFormXL - Sprint 44 Fase 3 Vista 9.
// Hook form PO con load/save + msg auto-dismiss. Reusa /api/po backend.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../api/apiClient';

const MSG_AUTO_DISMISS_MS = 4000;
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  referencia: '', proveedor_id: null, proveedor_nombre: '',
  proveedor_email: '', proveedor_telefono: '',
  fecha_po: today(), moneda: 'USD', estado: 'draft', descripcion: '',
  total_po_moneda: 0, total_po_cop: 0,
  created_at: null, updated_at: null,
};

export default function usePOFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const poId = id ? Number(id) : null;

  const [form, setForm]   = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState('basicos');
  const [msg, setMsg]         = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  useEffect(() => {
    if (!poId) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    apiClient.get('/po/' + poId).then((res) => {
      if (!alive) return;
      const d = (res && res.data) || {};
      setForm({
        referencia: d.referencia || '',
        proveedor_id: d.proveedor_id || null,
        proveedor_nombre: d.proveedor_nombre || '',
        proveedor_email: d.proveedor_email || '',
        proveedor_telefono: d.proveedor_telefono || '',
        fecha_po: (d.fecha_po || today()).slice(0, 10),
        moneda: d.moneda || 'USD',
        estado: d.estado || 'draft',
        descripcion: d.descripcion || '',
        total_po_moneda: d.total_po_moneda || 0,
        total_po_cop: d.total_po_cop || 0,
        created_at: d.created_at || null,
        updated_at: d.updated_at || null,
      });
    }).catch((e) => {
      if (alive) pushMsg('err', (e && e.message) || 'Error al cargar');
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [poId, pushMsg]);

  const set = useCallback((k, v) => setForm((p) => ({ ...p, [k]: v })), []);

  const onSave = useCallback(async () => {
    setSaving(true); setErrors({});
    try {
      const payload = {
        referencia: form.referencia,
        proveedor_id: form.proveedor_id,
        fecha_po: form.fecha_po || null,
        moneda: form.moneda,
        estado: form.estado,
        descripcion: form.descripcion,
      };
      let res;
      if (isEdit) res = await apiClient.put('/po/' + poId, payload);
      else res = await apiClient.post('/po/', payload);
      const newId = res && res.data && res.data.id;
      pushMsg('ok', isEdit ? 'PO actualizada' : 'PO creada');
      if (!isEdit && newId) navigate('/po/' + newId + '/edit', { replace: true });
    } catch (e) {
      const apiErrs = (e && e.data && e.data.errors) || null;
      if (apiErrs) setErrors(apiErrs);
      pushMsg('err', (e && e.data && e.data.message) || (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isEdit, poId, form, navigate, pushMsg]);

  return {
    poId, isEdit, navigate,
    form, set, errors,
    loading, saving,
    tab, setTab,
    msg, pushMsg, clearMsg: () => setMsg(null),
    onSave,
  };
}
